import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { RRule } from 'rrule';
import dayjs from 'dayjs';
import type { Database } from '@/integrations/supabase/types';

export type TaskStatus = 'Scheduled' | 'Due' | 'Overdue' | 'Done' | 'Skipped';
export type Task = Database['public']['Tables']['tasks']['Row'] & { 
  status: TaskStatus;
  owner?: { user_id: string; name: string | null; email: string } | null;
  last_completed_by_profile?: { user_id: string; name: string | null; email: string } | null;
};

interface TaskFilters {
  relationType?: string;
  relationId?: string;
}

interface CreateTaskData {
  title: string;
  description?: string;
  cadence: string;
  rrule?: string;
  owner?: string;
  owner_id?: string;
  due_next: string;
  status: TaskStatus;
  related_to_type?: string;
  related_to_id?: string;
  org_id: string;
}

interface UpdateTaskData {
  title?: string;
  description?: string;
  cadence?: string;
  rrule?: string;
  owner?: string;
  owner_id?: string;
  due_next?: string;
  status?: TaskStatus;
  related_to_type?: string;
  related_to_id?: string;
}

const calculateTaskStatus = (task: any): TaskStatus => {
  // If already marked Done or Skipped, keep that status
  if (task.status === 'Done' || task.status === 'Skipped') return task.status as TaskStatus;
  
  const now = dayjs();
  const dueDate = dayjs(task.due_next);
  
  if (dueDate.isBefore(now, 'day')) return 'Overdue';
  if (dueDate.isBefore(now.add(7, 'days'))) return 'Due';
  return 'Scheduled';
};

export const useTasks = (orgId?: string, filters?: TaskFilters, currentUserId?: string) => {
  const queryClient = useQueryClient();

  const tasksQuery = useQuery({
    queryKey: ['tasks', orgId, filters?.relationType, filters?.relationId],
    queryFn: async () => {
      if (!orgId) return [];
      
      let query = supabase
        .from('tasks')
        .select(`
          *,
          owner:profiles!tasks_owner_id_fkey(user_id, name, email),
          last_completed_by_profile:profiles!tasks_last_completed_by_fkey(user_id, name, email)
        `)
        .eq('org_id', orgId);

      if (filters?.relationType && filters?.relationId) {
        query = query
          .eq('related_to_type', filters.relationType)
          .eq('related_to_id', filters.relationId);
      }

      const { data, error } = await query.order('due_next', { ascending: true });

      if (error) throw error;

      return (data || []).map((taskRecord: any) => ({
        ...taskRecord,
        status: calculateTaskStatus(taskRecord),
        owner: Array.isArray(taskRecord.owner) ? taskRecord.owner[0] : taskRecord.owner,
        last_completed_by_profile: Array.isArray(taskRecord.last_completed_by_profile) 
          ? taskRecord.last_completed_by_profile[0] 
          : taskRecord.last_completed_by_profile,
      })) as Task[];
    },
    enabled: !!orgId,
  });

  const createTaskMutation = useMutation({
    mutationFn: async (data: CreateTaskData) => {
      const { data: createdTask, error } = await supabase.from('tasks').insert([{
        org_id: data.org_id,
        title: data.title,
        description: data.description ?? null,
        cadence: data.cadence,
        rrule: data.rrule ?? null,
        owner: data.owner ?? null,
        owner_id: data.owner_id ?? null,
        due_next: data.due_next,
        status: data.status,
        related_to_type: data.related_to_type ?? null,
        related_to_id: data.related_to_id ?? null,
      }]).select('id').single();
      
      if (error) throw error;
      return createdTask;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Task created successfully');
    },
    onError: (error) => {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateTaskData }) => {
      const { error } = await supabase
        .from('tasks')
        .update(data)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Task updated successfully');
    },
    onError: (error) => {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: async (id: string) => {
      // First delete related links
      await supabase.from('links').delete().or(`source_id.eq.${id},target_id.eq.${id}`);
      
      // Then delete the task
      const { error } = await supabase.from('tasks').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['links'] });
      toast.success('Task deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    },
  });

  const completeTaskMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const tasks = tasksQuery.data;
      const task = tasks?.find(t => t.id === id);
      
      if (!task) throw new Error('Task not found');

      // Permission check: must be owner or admin
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('user_id, role')
        .eq('user_id', currentUserId || '')
        .single();
      
      const isOwner = task.owner_id === currentUserId;
      const isAdmin = userProfile?.role === 'admin';
      
      if (!isOwner && !isAdmin) {
        throw new Error('Only task owner or admin can complete this task');
      }

      // Insert completion record for audit trail
      const { error: completionError } = await supabase
        .from('task_completions')
        .insert({
          task_id: id,
          org_id: task.org_id,
          completed_by: currentUserId,
          notes: notes ?? null,
        });
      
      if (completionError) throw completionError;

      // Check if this is a recurring task
      const isRecurring = task.cadence !== 'once';
      
      let nextDue = task.due_next;
      let newStatus: string = 'Done';
      let nextDueDate: Date | null = null;
      
      if (isRecurring) {
        const currentDue = dayjs(task.due_next);
        
        // Calculate next occurrence based on cadence or rrule
        if (task.rrule) {
          try {
            const rule = RRule.fromString(task.rrule);
            const nextOccurrence = rule.after(new Date(task.due_next), false);
            if (nextOccurrence) {
              nextDue = nextOccurrence.toISOString();
              nextDueDate = nextOccurrence;
            }
          } catch (err) {
            console.warn('Could not parse RRule for next occurrence:', err);
          }
        } else {
          // Calculate based on cadence
          switch (task.cadence) {
            case 'daily':
              nextDueDate = currentDue.add(1, 'day').toDate();
              break;
            case 'weekly':
              nextDueDate = currentDue.add(1, 'week').toDate();
              break;
            case 'monthly':
              nextDueDate = currentDue.add(1, 'month').toDate();
              break;
            case 'quarterly':
              nextDueDate = currentDue.add(3, 'months').toDate();
              break;
            case 'yearly':
              nextDueDate = currentDue.add(1, 'year').toDate();
              break;
          }
          
          if (nextDueDate) {
            nextDue = nextDueDate.toISOString();
          }
        }
        
        if (nextDueDate) {
          newStatus = 'Scheduled'; // Reset to Scheduled for recurring tasks
        }
      }

      // Update task with completion tracking
      const { error } = await supabase
        .from('tasks')
        .update({
          status: newStatus,
          due_next: nextDue,
          times_completed: (task.times_completed || 0) + 1,
          last_completed_at: new Date().toISOString(),
          last_completed_by: currentUserId,
        })
        .eq('id', id);

      if (error) throw error;
      
      return { isRecurring, nextDueDate };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['my-responsibilities'] });
      if (data.isRecurring && data.nextDueDate) {
        toast.success(`Task completed! Next occurrence: ${dayjs(data.nextDueDate).format('MMM D, YYYY')}`);
      } else {
        toast.success('Task completed');
      }
    },
    onError: (error: Error) => {
      console.error('Error completing task:', error);
      toast.error(error.message || 'Failed to complete task');
    },
  });

  return {
    tasks: tasksQuery.data,
    isLoading: tasksQuery.isLoading,
    error: tasksQuery.error,
    createTask: createTaskMutation.mutateAsync,
    updateTask: updateTaskMutation.mutate,
    deleteTask: deleteTaskMutation.mutate,
    completeTask: completeTaskMutation.mutate,
    isCreating: createTaskMutation.isPending,
    isUpdating: updateTaskMutation.isPending,
    isDeleting: deleteTaskMutation.isPending,
    isCompleting: completeTaskMutation.isPending,
  };
}
